import { useEffect, useRef, useState } from "react";

/**
 * Animates a number from `from` to `to` over `duration` ms.
 * The animation re-runs whenever `runKey` changes (or `from`/`to` change).
 * Respects prefers-reduced-motion (returns final value immediately).
 */
export function useCountdown(
  from: number,
  to: number,
  duration = 1500,
  runKey: unknown = null,
): number {
  const [display, setDisplay] = useState(to);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || from === to) {
      setDisplay(to);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [from, to, duration, runKey]);

  return display;
}

export function formatINR(value: number): string {
  // Returns the formatted number only (no currency symbol).
  // Call sites prefix `₹` themselves; including it here caused `₹₹` double-symbol bugs.
  return Math.round(value).toLocaleString("en-IN");
}
